import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { analytics } from "@/instrument";

/**
 * Resets window scroll to (0, 0) on every route change.
 *
 * React Router preserves scroll position across navigation by default,
 * which is correct for SPAs that need to restore state on Back/Forward,
 * but wrong for our use case: footer / nav links that take a user from a
 * scrolled-to-bottom landing page directly to a new page open in the
 * same scroll position. The new page would render with the viewport
 * stuck near its bottom, hiding the actual page content.
 *
 * Skips reset on hash navigation (e.g. /#services) so in-page anchor
 * links continue to land on their targets, and on POP (browser back/
 * forward) so React Router's native scroll restoration still works.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    // Use 'instant' so consecutive navigations feel snappy and don't
    // animate from wherever the user happened to scroll to.
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    // Fire a sanitised PostHog pageview on every route change. No-op when
    // PostHog isn't configured.
    analytics.page(pathname);
  }, [pathname, hash]);

  return null;
}
